import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { CacheKey } from '../types/cache-keys';
import { fetchItems } from '../support/queries';
import { useFetchUser } from './user-hooks';

export const ASSIGNMENT_LEVEL = {
  DomainSpecialist: 1,
  IncubationTeam: 2,
  BusinessUnitTme: 3,
} as const;

export const SUPPORT_PATHS_STALE_TIME = 10 * 60 * 1000; // 10 minutes
export const SUPPORT_PATHS_CACHE_TIME = 60 * 60 * 1000; // 1 hour

export type SupportPathMember = {
  id: number;
  email: string;
};

export type ProductSupportPath = {
  id: number;
  product?: {
    id: number;
    name: string;
  } | null;
  assignment_type?: {
    id: number;
    name: string;
    description?: string | null;
    level: number;
  } | null;
  user_group?: {
    id: number;
    name: string;
  } | null;
  azure_ad_group?: {
    id: number;
    name: string;
  } | null;
  product_name?: string | null;
  assignment_type_name?: string | null;
  assignment_type_level: number;
  team_name?: string | null;
  team_email?: string | null;
  user_group_name?: string | null;
  azure_ad_group_name?: string | null;
  group_source?: string | null;
  group_members?: SupportPathMember[];
  current_user_is_member?: boolean;
  current_user_can_manage?: boolean;
  is_active?: boolean;
  last_updated?: string;
  updated_by?: string | null;
};

export type ProductSupportPathQueue = {
  queueKey: string;
  assignment_type_level: number;
  assignment_type_name?: string | null;
  team_name?: string | null;
  team_email?: string | null;
  user_group?: ProductSupportPath['user_group'];
  azure_ad_group?: ProductSupportPath['azure_ad_group'];
  user_group_name?: string | null;
  azure_ad_group_name?: string | null;
  group_source?: string | null;
  group_members: SupportPathMember[];
  supportPathIds: number[];
  supportPaths: ProductSupportPath[];
  product_names: string[];
  title: string;
};

const PRODUCT_SUPPORT_PATH_QUERY_PARAMS = {
  detailed: true,
  depth: 1,
  include_group_members: true,
};

const normalizeTextKey = (value?: string | null) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeEmail = (email?: string | null) =>
  String(email ?? '').trim().toLowerCase();

const normalizeNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const dedupeMembers = (members: SupportPathMember[] = []) => {
  const byKey = new Map<string, SupportPathMember>();

  members.forEach((member) => {
    const id = normalizeNumber(member?.id);
    const email = normalizeEmail(member?.email);

    if (!id && !email) return;

    const key = id ? `id:${id}` : `email:${email}`;

    if (!byKey.has(key)) {
      byKey.set(key, member);
    }
  });

  return Array.from(byKey.values()).sort((a, b) =>
    String(a.email ?? '').localeCompare(String(b.email ?? ''))
  );
};

const dedupeStrings = (values: Array<string | null | undefined>) => {
  return Array.from(
    new Set(
      values
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));
};

const isCurrentUserMemberOfPath = (
  path: ProductSupportPath,
  user: any
): boolean => {
  if (!path || !user) return false;

  if (path.current_user_is_member === true) return true;

  const myEmail = normalizeEmail(user?.email);
  const myUserId = normalizeNumber(user?.id);

  const members = path.group_members ?? [];

  return members.some((member) => {
    const memberEmail = normalizeEmail(member?.email);
    const memberId = normalizeNumber(member?.id);

    if (myUserId && memberId && myUserId === memberId) return true;
    if (myEmail && memberEmail && myEmail === memberEmail) return true;

    return false;
  });
};

export const getSupportPathQueueKey = (path: ProductSupportPath) => {
  const level = path.assignment_type_level;

  // Prefer Azure AD group because it is typically the durable team identity.
  if (path.azure_ad_group?.id) {
    return `level-${level}__aad-${path.azure_ad_group.id}`;
  }

  if (path.user_group?.id) {
    return `level-${level}__ug-${path.user_group.id}`;
  }

  if (path.azure_ad_group_name) {
    return `level-${level}__aad-name-${normalizeTextKey(path.azure_ad_group_name)}`;
  }

  if (path.user_group_name) {
    return `level-${level}__ug-name-${normalizeTextKey(path.user_group_name)}`;
  }

  if (path.team_email) {
    return `level-${level}__email-${normalizeTextKey(path.team_email)}`;
  }

  if (path.team_name) {
    return `level-${level}__team-${normalizeTextKey(path.team_name)}`;
  }

  return `level-${level}__support-path-${path.id}`;
};

export const formatAssignmentTypeLabel = (
  level?: number,
  fallback?: string | null
) => {
  if (level === ASSIGNMENT_LEVEL.IncubationTeam) return 'Incubation Team';
  if (level === ASSIGNMENT_LEVEL.DomainSpecialist) return 'Domain Specialist';
  if (level === ASSIGNMENT_LEVEL.BusinessUnitTme) return 'Business Unit TME';
  return fallback || 'Support Team';
};

export const formatSupportPathQueueTitle = (queue: ProductSupportPathQueue) => {
  const typeLabel = formatAssignmentTypeLabel(
    queue.assignment_type_level,
    queue.assignment_type_name
  );

  const teamLabel =
    queue.team_name ||
    queue.azure_ad_group_name ||
    queue.user_group_name ||
    queue.team_email ||
    `Queue ${queue.queueKey}`;

  return `${typeLabel}: ${teamLabel}`;
};

export const buildSupportPathQueues = (
  paths: ProductSupportPath[] = []
): ProductSupportPathQueue[] => {
  const queueMap = new Map<string, ProductSupportPathQueue>();

  paths.forEach((path) => {
    const queueKey = getSupportPathQueueKey(path);
    const existing = queueMap.get(queueKey);

    if (!existing) {
      const queue: ProductSupportPathQueue = {
        queueKey,
        assignment_type_level: path.assignment_type_level,
        assignment_type_name: path.assignment_type_name,
        team_name: path.team_name,
        team_email: path.team_email,
        user_group: path.user_group,
        azure_ad_group: path.azure_ad_group,
        user_group_name: path.user_group_name,
        azure_ad_group_name: path.azure_ad_group_name,
        group_source: path.group_source,
        group_members: dedupeMembers(path.group_members ?? []),
        supportPathIds: [path.id],
        supportPaths: [path],
        product_names: dedupeStrings([path.product_name]),
        title: '',
      };

      queue.title = formatSupportPathQueueTitle(queue);
      queueMap.set(queueKey, queue);
      return;
    }

    existing.supportPaths.push(path);

    existing.supportPathIds = Array.from(
      new Set([...existing.supportPathIds, path.id])
    ).sort((a, b) => a - b);

    existing.group_members = dedupeMembers([
      ...existing.group_members,
      ...(path.group_members ?? []),
    ]);

    existing.product_names = dedupeStrings([
      ...existing.product_names,
      path.product_name,
    ]);

    existing.team_name = existing.team_name || path.team_name;
    existing.team_email = existing.team_email || path.team_email;
    existing.user_group = existing.user_group || path.user_group;
    existing.azure_ad_group = existing.azure_ad_group || path.azure_ad_group;
    existing.user_group_name = existing.user_group_name || path.user_group_name;
    existing.azure_ad_group_name =
      existing.azure_ad_group_name || path.azure_ad_group_name;
    existing.group_source = existing.group_source || path.group_source;

    existing.title = formatSupportPathQueueTitle(existing);
  });

  return Array.from(queueMap.values()).sort((a, b) => {
    if (a.assignment_type_level !== b.assignment_type_level) {
      return a.assignment_type_level - b.assignment_type_level;
    }

    return a.title.localeCompare(b.title);
  });
};

export const useFetchProductSupportPaths = (options: any = {}) => {
  return useQuery<ProductSupportPath[]>({
    queryKey: [
      CacheKey.ProductSupportPaths,
      PRODUCT_SUPPORT_PATH_QUERY_PARAMS,
    ],
    queryFn: () =>
      fetchItems(
        'db/escalation-product-support',
        PRODUCT_SUPPORT_PATH_QUERY_PARAMS,
        {}
      ),
    ...{
        staleTime: SUPPORT_PATHS_STALE_TIME,
        gcTime: SUPPORT_PATHS_CACHE_TIME,
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        ...options,
      },
  });
};

export const useMyProductSupportPaths = () => {
  const { data: user, isLoading: userLoading } = useFetchUser();

  const {
    data: productSupportPaths,
    isLoading: pathsLoading,
    error,
  } = useFetchProductSupportPaths({
    enabled: !!user,
  });

  const mySupportPaths = useMemo(() => {
    if (!user || !productSupportPaths) return [];

    return productSupportPaths
      .filter((path) => path.is_active !== false)
      .filter((path) => isCurrentUserMemberOfPath(path, user))
      .sort((a, b) => {
        const levelA = a.assignment_type_level ?? 999;
        const levelB = b.assignment_type_level ?? 999;

        if (levelA !== levelB) return levelA - levelB;

        const nameA =
          a.team_name ||
          a.azure_ad_group_name ||
          a.user_group_name ||
          a.product_name ||
          '';

        const nameB =
          b.team_name ||
          b.azure_ad_group_name ||
          b.user_group_name ||
          b.product_name ||
          '';

        return nameA.localeCompare(nameB);
      });
  }, [user, productSupportPaths]);

  return {
    data: mySupportPaths,
    user,
    isLoading: userLoading || pathsLoading,
    error,
  };
};

export const useMyProductSupportPathQueues = () => {
  const {
    data: mySupportPaths = [],
    user,
    isLoading,
    error,
  } = useMyProductSupportPaths();

  const queues = useMemo(() => {
    return buildSupportPathQueues(mySupportPaths).filter((queue) =>
      [
        ASSIGNMENT_LEVEL.DomainSpecialist,
        ASSIGNMENT_LEVEL.IncubationTeam,
      ].includes(queue.assignment_type_level as any)
    );
  }, [mySupportPaths]);

  return {
    data: queues,
    supportPaths: mySupportPaths,
    user,
    isLoading,
    error,
  };
};